/**
 * Extrae un mensaje de error amigable de un error de axios.
 * Prioridad: detail del backend > códigos de estado conocidos > red/timeout > fallback
 *
 * @param {Error} error - Error capturado (típicamente de axios)
 * @param {string} [fallback='Ocurrió un error inesperado'] - Mensaje por defecto
 * @returns {string} Mensaje en español para mostrar al usuario
 */
export function getErrorMessage(error, fallback = 'Ocurrió un error inesperado') {
  // Errores que no son de axios (TypeError, JSON parse, etc.)
  if (!error.response && !error.request) {
    return error.message || fallback;
  }

  // Error de red (no se recibió respuesta del servidor)
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return 'La solicitud tardó demasiado. Verifica tu conexión e intenta de nuevo.';
    }
    return 'Error de conexión. Verifica tu internet e intenta de nuevo.';
  }

  // El backend retornó un detail como string
  const detail = error.response?.data?.detail;
  if (detail && typeof detail === 'string') {
    return detail;
  }

  // Errores de validación (FastAPI retorna detail como array para 422)
  if (Array.isArray(detail)) {
    return detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
  }

  // Códigos de estado conocidos
  const status = error.response?.status;
  switch (status) {
    case 401: return 'Tu sesión ha expirado. Por favor inicia sesión nuevamente.';
    case 403: return 'No tienes permisos para realizar esta acción.';
    case 404: return 'El recurso solicitado no fue encontrado.';
    case 409: return 'Conflicto: el recurso ya existe o fue modificado.';
    case 413: return 'El archivo es demasiado grande.';
    case 422: return 'Los datos enviados no son válidos. Revisa el formulario.';
    case 429: return 'Demasiadas solicitudes. Espera un momento e intenta de nuevo.';
    case 500: return 'Error interno del servidor. Intenta más tarde.';
    case 503: return 'El sistema se encuentra en mantenimiento. Intenta más tarde.';
    default: return fallback;
  }
}
